import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { api, generateMockOrders } from '../services/api';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { cn } from '../lib/utils';
import { useSettings } from '../context/SettingsContext';

const { FiLoader, FiMaximize, FiMinimize, FiChevronDown } = FiIcons;

const CustomerDisplay = ({ selectedDate }) => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [mobileView, setMobileView] = useState('in-progress'); // 'in-progress' | 'ready'
  const { isTestData } = useSettings();

  const loadOrders = useCallback(async (isSilent = false) => {
    if (!isSilent) setLoading(true);
    try {
      let data = [];
      if (isTestData) {
        data = generateMockOrders(selectedDate, selectedDate, 15);
      } else {
        data = await api.fetchOrders(selectedDate);
      }
      setOrders(data);
    } catch (err) {
      console.error('Failed to load orders for display:', err);
    } finally {
      setLoading(false);
    }
  }, [selectedDate, isTestData]);

  useEffect(() => {
    loadOrders();
    const interval = setInterval(() => loadOrders(true), 10000);
    return () => clearInterval(interval);
  }, [loadOrders]);

  useEffect(() => {
    const handleRefresh = () => loadOrders(true);
    window.addEventListener('kanban-refresh-trigger', handleRefresh);
    return () => window.removeEventListener('kanban-refresh-trigger', handleRefresh);
  }, [loadOrders]);

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen().catch(() => {});
    } else {
      document.exitFullscreen();
    }
  };

  useEffect(() => {
    const handleFullscreenChange = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  if (loading && orders.length === 0) {
    return (
      <div className="h-full flex flex-col items-center justify-center text-yellow-400 bg-black">
        <SafeIcon icon={FiLoader} className="animate-spin mb-4" size={60} />
        <p className="text-2xl font-black uppercase tracking-widest">Loading Board...</p>
      </div>
    );
  }

  const inProgressStatuses = ['Received', 'Preparing', 'Delivering'];
  const readyStatuses = ['Completed'];

  const inProgressOrders = orders
    .filter(o => inProgressStatuses.includes(o.status))
    .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

  const readyOrders = orders
    .filter(o => readyStatuses.includes(o.status))
    .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

  const OrderTicket = ({ order, isReady }) => (
    <motion.div
      layout
      initial={{ opacity: 0, scale: 0.8 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.8 }}
      transition={{ type: "spring", stiffness: 300, damping: 25 }}
      className={cn(
        "rounded-2xl p-3 lg:p-6 flex flex-col items-center justify-center text-center shadow-xl border-2 w-full aspect-[4/3] lg:aspect-auto",
        isReady 
          ? "bg-yellow-400 border-yellow-300 text-black shadow-yellow-400/20" 
          : "bg-zinc-900 border-zinc-700 text-white"
      )}
    >
      <div className={cn(
        "text-2xl md:text-3xl lg:text-5xl font-black tracking-tighter mb-1 lg:mb-2",
        !isReady && "text-yellow-400"
      )}>
        #{order.tilda_order_id}
      </div>
      <div className={cn(
        "text-sm md:text-lg lg:text-2xl font-bold truncate w-full px-1 lg:px-2",
        isReady ? "text-black/60" : "text-zinc-400"
      )}>
        {order.name || 'Guest'}
      </div>
    </motion.div>
  );

  return (
    <div className="h-full flex flex-col bg-black overflow-hidden relative">
      {/* Кнопка Fullscreen в правом нижнем углу */}
      <div className="absolute bottom-6 right-6 z-50">
        <button 
          onClick={toggleFullscreen}
          className="p-4 bg-zinc-800/80 hover:bg-zinc-700 text-white rounded-full transition-all backdrop-blur-md shadow-2xl hover:scale-110 border border-zinc-600"
        >
          <SafeIcon icon={isFullscreen ? FiMinimize : FiMaximize} size={24} />
        </button>
      </div>

      {/* Выпадающий список для мобилок */}
      <div className="lg:hidden p-4 pb-0 z-10 shrink-0">
        <div className="relative">
          <select
            value={mobileView}
            onChange={(e) => setMobileView(e.target.value)}
            className="w-full bg-zinc-900 border-2 border-zinc-800 text-white font-black uppercase tracking-widest p-4 rounded-xl appearance-none outline-none focus:border-yellow-400 transition-colors"
          >
            <option value="in-progress">In Progress ({inProgressOrders.length})</option>
            <option value="ready">Ready ({readyOrders.length})</option>
          </select>
          <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none bg-zinc-900 pl-2">
            <SafeIcon icon={FiChevronDown} className="text-yellow-400" size={24} />
          </div>
        </div>
      </div>

      <div className="flex-1 flex flex-col lg:flex-row gap-4 lg:gap-8 p-4 lg:p-8 min-h-0">
        {/* Колонка In Progress */}
        <div className={cn(
          "flex-1 flex-col min-h-0 bg-zinc-950 rounded-3xl border border-zinc-800 overflow-hidden",
          mobileView === 'in-progress' ? 'flex' : 'hidden lg:flex'
        )}>
          <div className="bg-zinc-900 py-4 lg:py-6 text-center border-b border-zinc-800 shrink-0">
            <h2 className="text-2xl lg:text-5xl font-black text-white uppercase tracking-widest">In Progress</h2>
          </div>
          <div className="flex-1 overflow-y-auto scrollbar-hide p-4 lg:p-6">
            <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-3 lg:gap-6 items-start content-start">
              <AnimatePresence mode="popLayout">
                {inProgressOrders.map(order => <OrderTicket key={order.id || order._id} order={order} isReady={false} />)}
              </AnimatePresence>
            </div>
          </div>
        </div>

        {/* Колонка Ready */}
        <div className={cn(
          "flex-1 flex-col min-h-0 bg-yellow-950/10 rounded-3xl border border-yellow-900/30 overflow-hidden",
          mobileView === 'ready' ? 'flex' : 'hidden lg:flex'
        )}>
          <div className="bg-yellow-400 py-4 lg:py-6 text-center border-b border-yellow-500 shrink-0">
            <h2 className="text-2xl lg:text-5xl font-black text-black uppercase tracking-widest">Ready</h2>
          </div>
          <div className="flex-1 overflow-y-auto scrollbar-hide p-4 lg:p-6">
            <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-3 lg:gap-6 items-start content-start">
              <AnimatePresence mode="popLayout">
                {readyOrders.map(order => <OrderTicket key={order.id || order._id} order={order} isReady={true} />)}
              </AnimatePresence>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomerDisplay;